import type { ReactNode } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { IconSolidGithub } from '@gonfalon/observability-ui/components/icons/IconSolidGithub';
import type { ButtonProps, PopoverProps } from '@launchpad-ui/components';
import {
  Button,
  Focusable,
  Header,
  Menu as LDMenu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { buildGitHubConnectUrl } from '../../githubConnectUrl';
import type { GitHubRepo } from '../../types';

import styles from './GitHubRepoSelector.module.css';

export interface GitHubRepoSelectorProps {
  repos: GitHubRepo[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  /** Content rendered above the menu list (e.g. a warning callout). */
  headerContent?: ReactNode;
  /** Popover placement relative to the trigger. Defaults to "bottom start". */
  placement?: PopoverProps['placement'];
  /** Whether the trigger button is disabled. */
  disabled?: boolean;
  /** CSS class applied to the trigger button. */
  triggerClassName?: string;
  /** GitHub icon to render in the trigger. Defaults to the built-in IconSolidGithub. */
  githubIcon?: ReactNode;
  label?: ReactNode;
  /** Override the default "Manage repositories" action. */
  onManage?: () => void;
  /** Trigger button size. Defaults to "medium". */
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  /**
   * Defaults to "multiple". "single" also drops Select all / Clear all, which have nothing to act
   * on, and picking a repository replaces the selection rather than adding to it.
   */
  selectionMode?: 'single' | 'multiple';
}

export function GitHubRepoSelector({
  repos,
  selectedKeys,
  onSelectionChange,
  headerContent,
  placement = 'bottom start',
  disabled,
  triggerClassName,
  githubIcon,
  label: labelOverride,
  onManage,
  size = 'medium',
  variant = 'minimal',
  selectionMode = 'multiple',
}: GitHubRepoSelectorProps) {
  const privateRepos = repos.filter((repo) => !repo.is_public);
  const publicRepos = repos.filter((repo) => repo.is_public);
  const privateRepoKeys = privateRepos.map((repo) => repo.key);

  const isSingleSelect = selectionMode === 'single';
  const selectable = selectedKeys.filter((key) => privateRepoKeys.includes(key));
  // The selection slot is shared with the other GitHub surfaces, which are multi-select, so a
  // single-select picker can be handed more than one. Showing the first keeps the trigger label and
  // the menu from disagreeing with each other.
  const effectiveSelected = isSingleSelect ? selectable.slice(0, 1) : selectable;
  const allSelected = privateRepoKeys.length > 0 && effectiveSelected.length === privateRepoKeys.length;

  const selectedCount = effectiveSelected.length;
  let label: string;
  if (selectedCount === 0) {
    label = isSingleSelect ? 'Select a repository' : 'No repositories';
  } else if (allSelected && privateRepoKeys.length > 1) {
    label = 'All repos';
  } else if (selectedCount === 1) {
    const repo = privateRepos.find((r) => effectiveSelected.includes(r.key));
    const fullName = repo?.name ?? '1 repository';
    label = fullName.includes('/') ? (fullName.split('/').pop() ?? fullName) : fullName;
  } else {
    label = `${selectedCount} repositories`;
  }

  const handleToggleAll = () => {
    onSelectionChange(allSelected ? [] : privateRepoKeys);
  };

  const handleManage = () => {
    if (onManage) {
      onManage();
      return;
    }
    window.location.href = buildGitHubConnectUrl();
  };

  const icon = githubIcon ?? <IconSolidGithub size={14} />;

  return (
    <MenuTrigger>
      <Button variant={variant} size={size} isDisabled={disabled} className={triggerClassName}>
        {icon} {labelOverride ?? label}
        <Icon name="chevron-down" size="small" slot="end-icon" />
      </Button>
      <Popover placement={placement}>
        <div className={styles.popoverContent}>
          <div className={styles.headerActions}>
            {!isSingleSelect && (
              <Button variant="minimal" size="small" onPress={handleToggleAll}>
                {allSelected ? 'Clear all' : 'Select all'}
              </Button>
            )}
            <Button variant="minimal" size="small" onPress={handleManage}>
              {msg('integrations.githubRepoPicker.manageRepositories')}{' '}
              <Icon name="link-external" size="small" slot="end-icon" />
            </Button>
          </div>
          {headerContent}
          <LDMenu
            selectionMode={selectionMode}
            selectedKeys={effectiveSelected}
            onSelectionChange={(keys) => {
              if (keys === 'all') {
                onSelectionChange(isSingleSelect ? privateRepoKeys.slice(0, 1) : privateRepoKeys);
                return;
              }
              const selected = Array.from(keys).map(String);
              onSelectionChange(isSingleSelect ? selected.slice(0, 1) : selected);
            }}
            style={{ maxHeight: '40vh' }}
            disabledKeys={publicRepos.map((repo) => repo.key)}
          >
            <MenuSection>
              {privateRepos.map((repo) => (
                <MenuItem key={repo.key} id={repo.key}>
                  {repo.name}
                </MenuItem>
              ))}
            </MenuSection>
            {publicRepos.length > 0 && (
              <MenuSection aria-label={msg('integrations.githubRepoPicker.publicHeading')}>
                <Header>
                  <Message id="integrations.githubRepoPicker.publicHeading" />
                </Header>
                {publicRepos.map((repo) => (
                  <MenuItem key={repo.key} id={repo.key} textValue={repo.name}>
                    <TooltipTrigger>
                      <Focusable>
                        <span>{repo.name}</span>
                      </Focusable>
                      <Tooltip placement="right">
                        <Message id="integrations.githubRepoPicker.publicHint" />
                      </Tooltip>
                    </TooltipTrigger>
                  </MenuItem>
                ))}
              </MenuSection>
            )}
          </LDMenu>
        </div>
      </Popover>
    </MenuTrigger>
  );
}
